import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, Badge } from '@agion/ui'
import { cn } from '@/core/analytics_utils'
import type { ServiceStatus } from '@/core/observability_types'

interface ServiceHealthGridProps {
  services: ServiceStatus[]
  loading: boolean
  onServiceClick: (name: string) => void
}

const StatusDot: React.FC<{ status: ServiceStatus['status'] }> = ({ status }) => (
  <span
    className={cn(
      'inline-block h-2.5 w-2.5 rounded-full',
      status === 'up' && 'bg-emerald-500',
      status === 'down' && 'bg-red-500',
      status === 'unknown' && 'bg-slate-400'
    )}
  />
)

const ServiceCard: React.FC<{
  service: ServiceStatus
  onClick: () => void
}> = ({ service, onClick }) => {
  const { t } = useTranslation('pages')
  const errorPct = (service.errorRate * 100).toFixed(2)

  return (
    <Card
      className="cursor-pointer hover:ring-1 hover:ring-slate-300 dark:hover:ring-slate-600 transition-shadow"
      onClick={onClick}
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <StatusDot status={service.status} />
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
              {service.name}
            </span>
          </div>
          <Badge variant={service.status === 'up' ? 'default' : 'destructive'}>
            {service.status}
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <p className="text-slate-400 dark:text-slate-500">{t('observability.rate', 'Rate')}</p>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <p className="font-medium text-slate-700 dark:text-slate-300">{`${service.requestRate.toFixed(1)}/s`}</p>
          </div>
          <div>
            <p className="text-slate-400 dark:text-slate-500">{t('observability.errors', 'Errors')}</p>
            <p className={cn(
              'font-medium',
              service.errorRate > 0.05 ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'
            )}>
              {errorPct}%
            </p>
          </div>
          <div>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <p className="text-slate-400 dark:text-slate-500">P95</p>
            <p className="font-medium text-slate-700 dark:text-slate-300">
              {service.latencyP95 < 1000 ? `${service.latencyP95.toFixed(0)}ms` : `${(service.latencyP95 / 1000).toFixed(2)}s`}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const ServiceHealthGrid: React.FC<ServiceHealthGridProps> = ({ services, loading, onServiceClick }) => {
  const { t } = useTranslation('pages')

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i}>
            <CardContent className="py-4">
              <div className="h-20 animate-pulse bg-slate-100 dark:bg-slate-700 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (services.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-slate-500 dark:text-slate-400">
            {t('observability.noServices', 'No services found. LGTM stack may not be receiving metrics yet.')}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {services.map(svc => (
        <ServiceCard key={svc.name} service={svc} onClick={() => onServiceClick(svc.name)} />
      ))}
    </div>
  )
}
